<template>
  <div style="position: relative">
    <!-- Pin Button -->
    <v-tooltip bottom>
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          @click.stop="$emit('toggle-pin', province)"
          :color="isPinned ? '#fddf00' : 'white'"
          size="small"
          icon
          class="pin-button"
        >
          <v-icon :color="isPinned ? 'black' : '#666'" size="small">
            {{ isPinned ? "mdi-pin" : "mdi-pin-outline" }}
          </v-icon>
        </v-btn>
      </template>
      <span>{{
        isPinned ? "Unpin from monitoring" : "Pin for monitoring"
      }}</span>
    </v-tooltip>

    <!-- Province Card -->
    <v-card
      class="province-card"
      :class="cardClasses"
      @click="handleCardClick"
      :disabled="isDisabled"
      hover
      elevation="2"
    >
      <!-- Background Image -->
      <div class="province-image" :style="imageStyle"></div>

      <!-- Overlay -->
      <div class="province-overlay"></div>

      <!-- Loading Overlay -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-spinner"></div>
        <p class="loading-text">Connecting...</p>
      </div>

      <!-- Status Badges -->
      <div class="status-badges">
        <!-- Order Counter -->
        <div v-if="hasOrders && !isUnavailable" class="order-counter-badge">
          <v-chip
            color="#fddf00"
            variant="elevated"
            size="small"
            class="order-chip"
          >
            <span class="order-made-text">ORDER MADE</span>
            <span class="order-chip-text">{{ orderAmount }}</span>
          </v-chip>
        </div>

        <!-- Unavailable Badge -->
        <v-chip
          v-if="isUnavailable"
          color="error"
          variant="elevated"
          size="small"
          class="unavailable-badge"
        >
          <v-icon start>mdi-close-circle</v-icon>
          Unavailable
        </v-chip>
      </div>

      <!-- Province Info -->
      <div class="province-info">
        <h3 class="province-name">{{ province.province_name_en }}</h3>
        <p class="province-name-th">{{ province.province_name_th }}</p>

        <v-btn
          v-if="!isUnavailable"
          color="#fddf00"
          variant="elevated"
          size="small"
          class="select-btn"
        >
          <span class="select-btn-text">Select Province</span>
          <v-icon end color="black">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "ProvinceCard",
  props: {
    province: {
      type: Object,
      required: true,
    },
    orderAmount: {
      type: Number,
      default: 0,
    },
    isPinned: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isUnavailable() {
      return this.province.province_onuse === 2;
    },
    isDisabled() {
      return this.isUnavailable || this.isLoading;
    },
    hasOrders() {
      return this.orderAmount > 0;
    },
    cardClasses() {
      return {
        "province-card--disabled": this.isUnavailable,
        "province-card--has-orders": this.hasOrders,
        "province-card--loading": this.isLoading,
      };
    },
    imageStyle() {
      return {
        backgroundImage: `url(${this.province.province_image})`,
        filter: this.isUnavailable ? "grayscale(100%)" : "",
      };
    },
  },
  methods: {
    handleCardClick() {
      if (!this.isDisabled) {
        this.$emit("select", this.province);
      }
    },
  },
};
</script>

<style scoped>
.pin-button {
  position: absolute;
  top: -22px;
  left: -22px;
  z-index: 1;
}

.province-card {
  position: relative;
  height: 200px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 16px;
  overflow: hidden;
  z-index: 0;
}

.province-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(253, 223, 0, 0.4);
}

.province-card--disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.province-card--disabled:hover {
  transform: none;
}

.province-card--has-orders {
  border: 2px solid #fddf00;
  box-shadow: 0 4px 20px rgba(253, 223, 0, 0.3);
}

.province-card--loading {
  pointer-events: none;
  opacity: 0.8;
}

.province-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: transform 0.3s ease;
}

.province-card:hover .province-image {
  transform: scale(1.1);
}

.province-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.7) 0%,
    rgba(253, 223, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.8) 100%
  );
  z-index: 1;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 16px;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(253, 223, 0, 0.3);
  border-top: 4px solid #fddf00;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: white;
  font-size: 1rem;
  font-weight: 600;
  margin-top: 1rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.status-badges {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.order-chip {
  font-weight: 600;
  border: 2px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.order-made-text {
  color: black;
  font-weight: 600;
  font-size: 0.7rem;
  margin-right: 0.25rem;
}

.order-chip-text {
  color: black;
  font-weight: 700;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 0px 4px;
  margin-left: 0.25rem;
}

.province-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem 1.5rem 1.5rem;
  padding-top: 3rem;
  z-index: 2;
  color: white;
}

.province-name {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.province-name-th {
  font-size: 0.9rem;
  opacity: 0.9;
  margin-bottom: 1rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.select-btn {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.3s ease;
  border: 2px solid rgba(0, 0, 0, 0.1);
}

.select-btn-text {
  color: black;
  font-weight: 600;
}

.province-card:hover .select-btn {
  opacity: 1;
  transform: translateY(0);
  box-shadow: 0 4px 12px rgba(253, 223, 0, 0.4);
}
</style>
